<template>
  <Teleport to="body">
  <div class="modal-overlay">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">Delete Check</h2>
        <button class="close-btn" @click="handleClose" type="button">
          <img :src="fillIcon" alt="close" class="close-icon" />
        </button>
      </div>

      <div class="modal-body">
        <p class="delete-message">
          Are you sure you want to delete "{{ itemName }}"? Existing data will not be affected, but
          this item will no longer be available going forward.
        </p>
      </div>

      <div class="modal-footer">
        <button class="btn-no" @click="handleClose" type="button">No</button>
        <button class="btn-yes" :disabled="submitting" @click="handleDelete" type="button">
          <span v-if="submitting" class="spinner"></span>
          <span>{{ submitting ? '' : 'Yes' }}</span>
        </button>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { deleteChecklistItem } from '@/api/itemdelete'
import fillIcon from '@/assets/Fill 1@2x.png'

interface Props {
  itemId: string
  itemName: string
}
const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'deleted', success: boolean): void
  (e: 'toast', data: { type: 'success' | 'warning' | 'error'; message: string }): void
}>()

const submitting = ref(false)

function handleClose(): void {
  emit('close')
}

async function handleDelete(): Promise<void> {
  submitting.value = true
  try {
    const response = await deleteChecklistItem({ id: parseInt(props.itemId) })
    if (response.code === 200) {
      emit('deleted', true)
      setTimeout(() => emit('close'), 300)
    } else {
      emit('deleted', false)
      emit('toast', { type: 'error', message: response.msg || 'Failed to delete item' })
      setTimeout(() => emit('close'), 300)
    }
  } catch (error) {
    console.error('Delete error:', error)
    emit('deleted', false)
    emit('toast', { type: 'error', message: 'Failed to delete item' })
    setTimeout(() => emit('close'), 300)
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
}

.modal-container {
  background-color: #ffffff;
  border-radius: 12px;
  width: 530px;
  min-height: 212px;
  height: auto;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
}
@media (max-width: 576px) {
  .modal-container {
    width: 100%;
    height: auto;
    min-height: 212px;
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  padding: 24px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
}
.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  box-sizing: border-box;
}

.delete-message {
  font-size: 14px;
  font-weight: 400;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  color: #000000;
  line-height: 20px;
margin: 0 12px -24px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 24px 24px 24px;
  flex-shrink: 0;
}

.btn-no {
  width: 80px;
  height: 32px;
  padding: 0;
  border: 1px solid #000000;
  border-radius: 24px;
  background-color: #ffffff;
  color: #333;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
}
.btn-no:hover {
  background-color: #000000;
  color: #ffffff;
}

.btn-yes {
  width: 80px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 24px;
  background-color: #000000;
  color: #ffffff;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    SFPro,
    'SF Pro',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-yes:hover {
  background-color: #484848;
}
.btn-yes:disabled {
  cursor: not-allowed;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
